import { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';

/**
 * Shared data-fetching hook for contractor and homeowner dashboards.
 * Handles projects, estimates, and notifications — the three things both roles need.
 */
const useDashboardData = () => {
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [estimateSearchQuery, setEstimateSearchQuery] = useState('');
  const [projectFilters, setProjectFilters] = useState({ status: 'all', sort: 'created_at', order: 'desc' });
  const [estimateFilters, setEstimateFilters] = useState({ status: 'all', sort: 'created_at', order: 'desc', sort_cost: true });

  const [notificationsAnchor, setNotificationsAnchor] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/projects', {
        params: {
          q: searchQuery || undefined,
          status: projectFilters.status !== 'all' ? projectFilters.status : undefined,
          sort: projectFilters.sort,
          order: projectFilters.order,
        },
      });
      setProjects(response.data.projects);
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, projectFilters]);

  const fetchEstimates = useCallback(async () => {
    try {
      const response = await axios.get('/api/estimates', {
        params: {
          q: estimateSearchQuery || undefined,
          status: estimateFilters.status !== 'all' ? estimateFilters.status : undefined,
          sort: estimateFilters.sort,
          order: estimateFilters.order,
        },
      });
      setEstimates(response.data.estimates);
    } catch (err) {
      setError('Failed to load estimates');
    }
  }, [estimateSearchQuery, estimateFilters]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data.notifications);
    } catch (err) {
      // non-critical — don't surface to user
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get('/api/notifications/unread-count');
      setUnreadCount(response.data.count);
    } catch (err) {
      // non-critical
    }
  }, []);

  const handleMarkAsRead = useCallback(async (notificationId) => {
    try {
      await axios.put(`/api/notifications/${notificationId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (err) {
      // non-critical
    }
  }, [fetchNotifications, fetchUnreadCount]);

  const handleNotificationsClick = (event) => setNotificationsAnchor(event.currentTarget);
  const handleNotificationsClose = () => setNotificationsAnchor(null);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  return {
    // data
    projects, estimates, notifications, unreadCount,
    // status
    loading, error, setError,
    // filters
    searchQuery, setSearchQuery,
    estimateSearchQuery, setEstimateSearchQuery,
    projectFilters, setProjectFilters,
    estimateFilters, setEstimateFilters,
    // refetch
    fetchProjects, fetchEstimates,
    // notifications UI
    notificationsAnchor,
    handleNotificationsClick,
    handleNotificationsClose,
    handleMarkAsRead,
  };
};

export default useDashboardData;
